import { action, makeObservable, observable } from 'mobx';

const RETURN_URL_STORAGE_KEY = 'return-url';
const USER_ACCESS_TOKEN_STORAGE_KEY = 'access-token';
const FULL_ACCESS_PERMISSION_KEY = 'FullAccess';

const supportedTokenKinds =
{
  'internal': 1,
};

interface AuthorizationApiJwt
{
  kind: string;
  ver: number;
  jti: string;
  iss: string;
  sub: string;
  aud: string;
  nbf: number;
  exp: number;
  iat: number;
  instance: string;
  display_name: string;
  permissions: string[];
}
interface User
{
  identity: string;
  displayName: string;
}

const parseJwt = (token: string) =>
{
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(window.atob(base64).split('').map((c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
  const parsedToken = JSON.parse(jsonPayload);
  return validateToken({...parsedToken, kind: 'internal', instance: parsedToken.sub, display_name: parsedToken.sub, permissions: [FULL_ACCESS_PERMISSION_KEY]} as AuthorizationApiJwt);
}
const validateToken = (token: AuthorizationApiJwt) =>
{
  const isSupported = Object.keys(supportedTokenKinds).includes(token.kind)
      && supportedTokenKinds[token.kind as keyof typeof supportedTokenKinds] <= token.ver
      ;
  if (!isSupported)
  {
    throw new Error('unsupported JWT provided');
  }
  if (token.exp < (new Date()).getTime() / 1000)
  {
    throw new Error('expired JWT token')
  }
  return token;
}

export interface IAuthStore
{
  returnUrl?: string;
  accessToken?: string;
  currentUser?: User;
  permissions: string[];

  setJwt: (token: string | null | undefined) => void;
  setReturnUrl: (url?: string) => void;
  hasPermission: (permission: string, scope?: string) => boolean;
  hasPermissions: (permissions: string[], scope?: string) => boolean;
  hasAnyPermission: (permissions: string[], scope?: string) => boolean;
}

export class AuthStore implements IAuthStore
{
  private keyPrefix: string;
  private permissionPrefix: string;
  private localStorage = window.localStorage;

  returnUrl?: string = undefined;
  accessToken?: string = undefined;
  currentUser?: User = undefined;
  permissions: string[] = [];

  constructor(keyPrefix?: string, permissionPrefix?: string)
  {
    this.keyPrefix = keyPrefix ?? '';
    this.permissionPrefix = permissionPrefix ?? '';
    makeObservable(this,
      {
        accessToken: observable,
        currentUser: observable,
        permissions: observable,
        setJwt: action,
        invalidate: action,
      });
    this.readAccessToken();
    this.readReturnUrl();
  }

  setJwt(jwt: string | null | undefined)
  {
    try
    {
      if (!jwt)
      {
        this.invalidate();
        return;
      }
      const token = parseJwt(jwt);
      this.currentUser = {identity: token.instance, displayName: token.display_name,};
      this.permissions = token.permissions.sort();
      this.accessToken = jwt;
      setTimeout(() => this.invalidate(), token.exp * 1000 - (new Date()).getTime());
      this.localStorage.setItem(this.makeKey(USER_ACCESS_TOKEN_STORAGE_KEY), jwt);
    }
    catch (e)
    {
      this.invalidate();
      throw e;
    }
  }

  setReturnUrl(url?: string)
  {
    this.returnUrl = url;
    if (url)
    {
      this.localStorage.setItem(this.makeKey(RETURN_URL_STORAGE_KEY), url);
    }
    else
    {
      this.localStorage.removeItem(this.makeKey(RETURN_URL_STORAGE_KEY));
    }
  }

  hasFullAccess(scope?: string)
  {
    const fullAccessPermissionNames = [FULL_ACCESS_PERMISSION_KEY, this.makePermissionKey(FULL_ACCESS_PERMISSION_KEY)];
    if (scope !== undefined)
    {
      fullAccessPermissionNames.push(`${scope}:${FULL_ACCESS_PERMISSION_KEY}`);
    }
    return this.permissions.find(x => fullAccessPermissionNames.includes(x)) !== undefined;
  }

  hasAnyPermission(permissions: string[], scope?: string)
  {
    if (this.hasFullAccess(scope))
    {
      return true;
    }
    const permissionNames = permissions.map(x => this.makePermissionKey(scope ? `${scope}:${x}` : x));
    return this.permissions.find(x => permissionNames.includes(x)) !== undefined;
  }
  hasPermission(permission: string, scope?: string)
  {
    return this.hasAnyPermission([permission], scope);
  }
  hasPermissions(permissions: string[], scope?: string)
  {
    if (this.hasFullAccess(scope))
    {
      return true;
    }
    const permissionNames = permissions.map(x => this.makePermissionKey(scope ? `${scope}:${x}` : x));
    return permissionNames.every(x => this.permissions.includes(x));
  }

  invalidate()
  {
    this.permissions = [];
    this.currentUser = undefined;
    this.accessToken = undefined;
    this.localStorage.removeItem(this.makeKey(USER_ACCESS_TOKEN_STORAGE_KEY));
  }

  private readAccessToken()
  {
    const storedToken = this.localStorage.getItem(this.makeKey(USER_ACCESS_TOKEN_STORAGE_KEY));
    try
    {
      this.setJwt(storedToken);
    }
    catch
    {
      this.localStorage.removeItem(this.makeKey(USER_ACCESS_TOKEN_STORAGE_KEY));
    }
  }
  private readReturnUrl()
  {
    const returnUrl = this.localStorage.getItem(this.makeKey(RETURN_URL_STORAGE_KEY));
    try
    {
      this.setReturnUrl(returnUrl ?? undefined);
    }
    catch
    {
      this.localStorage.removeItem(this.makeKey(RETURN_URL_STORAGE_KEY));
    }
  }
  private makeKey(key: string)
  {
    return `${this.keyPrefix}${key}`;
  }
  private makePermissionKey(key: string)
  {
    return `${this.permissionPrefix}${key}`;
  }
}

export const authStore = new AuthStore('fg-', 'feature-gating-ui:');
